/**
 * Front-end model allowing interaction with the permission system. See back-end complement for field descriptions.
 * @module app/models/Permission
 */
import Backbone from "backbone";
import _ from "underscore";
import $ from "jquery";
import auth from "app/auth";
import RequestUtils from "app/utils/RequestUtils";
import ErrorUtils from "app/utils/ErrorUtils";
import urlService from "app/UrlService";
var Permission = Backbone.Model.extend(
  {
    defaults: {
      consumer: null,
      type: null,
      producer: null,
      actions: {},
    },
  },
  {
    _request: function (models, type, actions, requestType, process, cb) {
      var deferred = new $.Deferred();
      var url = urlService.apiurl("/permission/set");
      var ids;
      var data;

      if (models) {
        if (!_.isArray(models)) {
          models = [models];
        }

        ids = _.map(models, function (model) {
          if (model == Permission) {
            return "undefined";
          } else {
            return model.id;
          }
        });
      } else {
        ids = ["undefined"]; //maybe?
      }

      data = {
        ids: ids,
        type: type,
      };

      if (actions) {
        data.permissions = actions;
      }

      $.ajax({
        url: url,
        contentType: "application/json",
        processData: false,
        type: requestType,
        data: JSON.stringify(data),
        headers: {
          Authorization: "Bearer " + auth.token,
        },
      })
        .done(function (data) {
          var result = data;

          if (_.isFunction(process)) {
            result = process(result);
          }

          deferred.resolve(result);

          if (_.isFunction(cb)) {
            cb(null, result);
          }
        })
        .fail(function (xhr) {
          var err = ErrorUtils.normalizeError({}, xhr);
          deferred.reject(err);

          if (_.isFunction(cb)) {
            cb(err);
          }
        });

      return deferred;
    },

    /**
     * Obtains the set of consumers which have access to the given type, along with their access type.
     * @param {String|Object} type A type or mapping from type to id used to locate the entry.
     * @param {Function} [cb] Function expecting two arguments. The first is an error, if any, and the second is a hash with keys "global" "type" and "item". Each value is an object mapping the keys "users" and "groups" to separate hashes. "public" is mapped directly to a permission hash, while the others are mapped to hashes which map from model ID to permission hash. A permission hash is an object which maps strings to booleans.
     * @return {Promise} A promise object, which rejects with an error or resolves with the same result as provided in the callback.
     */
    findInverse: function (type, cb) {
      var deferred = new $.Deferred();
      var _cb = function (err, data) {
        if (err) {
          if (_.isFunction(cb)) {
            cb(err);
          }

          deferred.reject(err);
        } else {
          if (_.isFunction(cb)) {
            cb(null, data);
          }

          deferred.resolve(data);
        }
      };

      RequestUtils.post(urlService.apiurl("/permission/inverse"), {
        type: type,
      })
        .done(function (data) {
          _cb(null, data);
        })
        .fail(function (xhr) {
          _cb(ErrorUtils.normalizeError({}, xhr));
        });

      return deferred.promise();
    },

    /**
     * Obtains the permission set entry for each of the input models.
     * @param  {object|Array}   models A model or models for which to retrieve permission set(s).
     * @param  {string|object}   type   A type or mapping from type to id used to locate the entry.
     * @param  {Function} cb     Function expecting two arguments. The first is an error, if any, and the second is a mapping from model ID to permission set entry.
     * @return {Promise}          A promise object.
     */
    find: function (models, type, cb) {
      function returnData(models) {
        var result = {};

        _.each(models, function (model) {
          result[model.id] = model._permCache;
        });

        return result;
      }

      if (models) {
        if (!_.isArray(models)) {
          models = [models];
        }
      } else {
        models = [Permission]; // this is how we store "public" permissions
      }

      var sentModels = _.filter(models, function (model) {
        return !model._permCache;
      });
      var modelsById = {};

      _.each(models, function (model) {
        modelsById[model.id] = model;
      });
      var result;
      //we're caching the permission data
      //if all of our models have a cache, then skip the server request and just return
      //the cached data.
      if (sentModels.length) {
        result = Permission._request(
          sentModels,
          type,
          null,
          "POST",
          function (data) {
            _.each(data, function (permSet, key) {
              var model = modelsById[key];

              if (model) {
                model._permCache = permSet;
              }
            });

            return returnData(models);
          },
          cb
        );
      } else {
        result = new $.Deferred();
        var data = returnData(models);

        result.resolve(data);

        if (_.isFunction(cb)) {
          cb(null, data);
        }
      }

      return result;
    },

    /**
     * Updates the permission set entries for each of the input models.
     * @param  {object|Array}   models A model or models for which to update permission set(s).
     * @param  {string|object}   type   A type or mapping from type to id for the entry.
     * @param {object} actions A mapping from action name to boolean to update.
     * @param  {Function} cb     Function expecting one argument, which is an error, if any.
     * @return {Promise}          A promise object.
     */
    update: function (models, type, actions, cb) {
      if (models) {
        if (!_.isArray(models)) {
          models = [models];
        }
      } else {
        models = [Permission];
      }

      _.each(models, function (model) {
        delete model._permCache;
      });

      return Permission._request(models, type, actions, "PUT", null, cb);
    },

    /**
     * Deletes the permission set entries for each of the input models.
     * @param  {object|Array}   models A model or models for which to delete permission set(s).
     * @param  {string|object}   type   A type or mapping from type to id for the entry.
     * @param {object} actions A mapping from action name to boolean to delete.
     * @param  {Function} cb     Function expecting one argument, which is an error, if any.
     * @return {Promise}          A promise object.
     */
    destroy: function (models, type, actions, cb) {
      if (models) {
        if (!_.isArray(models)) {
          models = [models];
        }
      } else {
        models = [Permission];
      }

      _.each(models, function (model) {
        delete model._permCache;
      });

      return Permission._request(models, type, actions, "DELETE", null, cb);
    },

    /**
     * Clear the permission cache for a set of models.
     * @param  {object | Array} [models] The model or models to clear. If not specified, the global permission cache will be cleared.
     */
    clear: function (models) {
      if (models) {
        if (!_.isArray(models)) {
          models = [models];
        }
      } else {
        models = [Permission];
      }

      _.each(models, function (model) {
        delete model._permCache;
      });
    },
  }
);

export default Permission;



//////////////////
// WEBPACK FOOTER
// ./public/js/app/models/Permission.js
// module id = 106
// module chunks = 1